import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PrefetchLink from '@/components/PrefetchLink';
import { calcHeadcount } from '@/lib/calcHeadcount';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Calendar, Users, MapPin, Sparkles, Download, Smartphone, Globe, Search, Plus, ChevronDown } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AIRecommendations from '@/components/AIRecommendations';
import { useAuth, API } from '@/App';
import AuthDialog from '@/components/AuthDialog';
import TutorialTour from '@/components/TutorialTour';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import { showLargeToast } from '@/components/ui/sonner';
import AppStoreBadges from '@/components/AppStoreBadges';

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showInstallPopup, setShowInstallPopup] = useState(false);
  const isIOSApp = useIsIOSApp();
  const [popupInitialized, setPopupInitialized] = useState(false);
  const [eventCount, setEventCount] = useState(0);
  const [localEvents, setLocalEvents] = useState([]);
  const [countryEvents, setCountryEvents] = useState([]);
  const [localExpats, setLocalExpats] = useState([]);
  const [detectedCity, setDetectedCity] = useState('');
  const [detectedCountry, setDetectedCountry] = useState('');
  const [loadingLocal, setLoadingLocal] = useState(true);
  // Only true after at least one successful events fetch — prevents the
  // "Be the first to host" card from flashing when a network blip errors
  // out silently while city state is already resolved.
  const [eventsFetchOk, setEventsFetchOk] = useState(false);
  const [expandedRecurring, setExpandedRecurring] = useState({});
  // Distance pill: '' (default: city + country split) | '25' | '50' | '100' (radius mode)
  const [distanceKm, setDistanceKm] = useState('');
  // City override — let users browse a different city than the auto-detected one (persisted to localStorage)
  const [overrideCity, setOverrideCity] = useState(() => {
    try { return localStorage.getItem('landingOverrideCity') || ''; } catch { return ''; }
  });
  const [overrideCountry, setOverrideCountry] = useState(() => {
    try { return localStorage.getItem('landingOverrideCountry') || ''; } catch { return ''; }
  });
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [cityPickerInput, setCityPickerInput] = useState('');
  const [countryPickerInput, setCountryPickerInput] = useState('');
  // Track which source detected the city: 'override' | 'geo' | 'profile' | ''
  const [locationSource, setLocationSource] = useState('');
  // Geolocation result — resolved ONCE on mount, sticky across user re-renders
  const [geoLocation, setGeoLocation] = useState(null); // { city, country } or null
  const [geoAttempted, setGeoAttempted] = useState(false);
  // One-time toast shown when geolocation succeeds AND user has a different profile city
  const [showGeoToast, setShowGeoToast] = useState(false);
  // Hard guard so geolocation cannot be set more than once per page load (defends against
  // React StrictMode double-mount, slow Nominatim retries, and pushed coordinate refinements).
  const geoFinalizedRef = useRef(false);
  // Monotonic generation counter — every priority-chain run bumps it.
  // Async fetches capture their generation and bail if a newer run has started,
  // so a slow "profile" fetch can never overwrite the state of a faster "geo" fetch.
  const fetchGenRef = useRef(0);

  // Show tutorial for new users
  useEffect(() => {
    if (user && !localStorage.getItem('tutorialCompleted')) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Fetch event count
  useEffect(() => {
    const fetchEventCount = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEventCount(response.data.length);
      } catch (error) {
        setEventCount(12); // Fallback
      }
    };
    fetchEventCount();
  }, []);

  // Geolocation — runs ONCE on mount, result is sticky across user re-renders.
  // Uses Photon (komoot) for reverse geocoding to stay consistent with backend and avoid
  // Nominatim's strict 503/CORS limits. A ref-based guard ensures only the FIRST successful
  // resolution wins, even if StrictMode double-mounts or the browser refines coordinates.
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoAttempted(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        if (geoFinalizedRef.current) return; // already resolved, ignore late callbacks
        try {
          const { latitude, longitude } = position.coords;
          // Photon reverse geocode (open, no key, lenient CORS)
          const geoRes = await fetch(
            `https://photon.komoot.io/reverse?lat=${latitude}&lon=${longitude}&lang=en`
          );
          const geoData = await geoRes.json();
          const props = geoData?.features?.[0]?.properties || {};
          const city = props.city || props.town || props.village || props.name || '';
          const country = props.country || '';
          if (geoFinalizedRef.current) return; // double-check after await
          if (city || country) {
            geoFinalizedRef.current = true;
            setGeoLocation({ city, country });
          }
        } catch {}
        setGeoAttempted(true);
      },
      () => { setGeoAttempted(true); },
      { timeout: 8000, maximumAge: 60000 }
    );
  }, []);

  // Fetch local events + expats
  useEffect(() => {
    const groupRecurring = (events) => {
      const grouped = [];
      const seenParents = new Set();
      for (const ev of events) {
        const parentId = ev.parent_event_id || ev.event_id;
        if (seenParents.has(parentId)) {
          const parent = grouped.find(g => (g.parent_event_id || g.event_id) === parentId || g.event_id === parentId);
          if (parent) {
            if (!parent._otherDates) parent._otherDates = [];
            parent._otherDates.push({ event_id: ev.event_id, event_date: ev.event_date, event_time: ev.event_time });
          }
          continue;
        }
        seenParents.add(parentId);
        grouped.push({ ...ev, _otherDates: [] });
      }
      return grouped;
    };
    
    const fetchEvents = async (city = '', country = '', gen = 0) => {
      let ok = false;
      try {
        if (city || country) {
          if (distanceKm && city) {
            // Radius mode: single sorted list of nearby events
            const rParams = new URLSearchParams();
            rParams.append('city', city);
            if (country) rParams.append('country', country);
            rParams.append('radius_km', distanceKm);
            const res = await axios.get(`${API}/events/by-radius?${rParams.toString()}`);
            if (gen !== fetchGenRef.current) return; // stale — newer run started
            const radiusList = groupRecurring(res.data.events || []);            setLocalEvents(radiusList.slice(0, 6));
            setCountryEvents([]);
            ok = true;
          } else {
            // Split-by-location endpoint: returns { city_events, country_events }
            const params = new URLSearchParams();
            if (city) params.append('city', city);
            if (country) params.append('country', country);
            const res = await axios.get(`${API}/events/by-location?${params.toString()}`);
            if (gen !== fetchGenRef.current) return; // stale — newer run started
            const cityList = groupRecurring(res.data.city_events || []);
            const countryList = groupRecurring(res.data.country_events || []);
            setLocalEvents(cityList.slice(0, 6));
            setCountryEvents(countryList.slice(0, 6));
            ok = true;
          }
        } else {
          // No location yet — fall back to recent events list
          const eventsRes = await axios.get(`${API}/events`);
          if (gen !== fetchGenRef.current) return;
          const events = eventsRes.data || [];
          setLocalEvents(groupRecurring(events).slice(0, 6));
          setCountryEvents([]);
          ok = true;
        }
      } catch {}
      if (gen === fetchGenRef.current) {
        setEventsFetchOk(ok);
        setLoadingLocal(false);
      }
    };

    const fetchExpats = async (city = '', country = '', gen = 0) => {
      if (!user) return;
      try {
        const params = new URLSearchParams();
        if (city) params.append('city', city);
        if (country) params.append('country', country);
        const res = await axios.get(`${API}/users/search?${params.toString()}`);
        if (gen !== fetchGenRef.current) return;
        let expats = (res.data?.users || []).filter(e => e.user_id !== user.user_id);
        if (expats.length === 0 && country && city) {
          const fallbackParams = new URLSearchParams();
          fallbackParams.append('country', country);
          const fallbackRes = await axios.get(`${API}/users/search?${fallbackParams.toString()}`);
          if (gen !== fetchGenRef.current) return;
          expats = (fallbackRes.data?.users || []).filter(e => e.user_id !== user.user_id);
        }
        setLocalExpats(expats.slice(0, 8));
      } catch { if (gen === fetchGenRef.current) setLocalExpats([]); }
    };

    // Bump generation so any in-flight fetches from a previous run are ignored.
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    // Reset the success flag so the empty-state card hides until THIS run confirms
    // an actually-empty response from the server.
    setEventsFetchOk(false);

    // Priority: manual override > browser geolocation > profile residence > nothing
    if (overrideCity || overrideCountry) {
      setDetectedCity(overrideCity || overrideCountry);
      setDetectedCountry(overrideCountry);
      setLocationSource('override');
      fetchEvents(overrideCity, overrideCountry, gen);
      fetchExpats(overrideCity, overrideCountry, gen);
      return;
    }
    
    // Geolocation result (resolved once, sticky)
    if (geoLocation && (geoLocation.city || geoLocation.country)) {
      setDetectedCity(geoLocation.city || geoLocation.country);
      setDetectedCountry(geoLocation.country);
      setLocationSource('geo');
      fetchEvents(geoLocation.city, geoLocation.country, gen);
      if (user) fetchExpats(geoLocation.city, geoLocation.country, gen);
      return;
    }
    
    // Profile residence — only used while geo is still pending OR if it failed
    if (user) {
      const profileCity = user.residence_city || '';
      const profileCountry = user.residence_country || '';
      if (profileCity || profileCountry) {
        setDetectedCity(profileCity || profileCountry);
        setDetectedCountry(profileCountry);
        setLocationSource('profile');
        fetchEvents(profileCity, profileCountry, gen);
        fetchExpats(profileCity, profileCountry, gen);
        return;
      }
    }
    
    // No location info at all — show generic recent events
    fetchEvents('', '', gen);
  }, [user, distanceKm, overrideCity, overrideCountry, geoLocation]);
  
  // One-time toast: show on first geo success if user has a different profile location.
  // Auto-dismisses after 6 seconds. Uses sessionStorage so it only shows once per browser session.
  useEffect(() => {
    if (!geoLocation || !user) return;
    if (!user.residence_city && !user.residence_country) return;
    const profileCity = (user.residence_city || '').toLowerCase().trim();
    const profileCountry = (user.residence_country || '').toLowerCase().trim();
    const geoCity = (geoLocation.city || '').toLowerCase().trim();
    const geoCountry = (geoLocation.country || '').toLowerCase().trim();
    // Show when there is *any* meaningful mismatch:
    //  - city differs (when both known), OR
    //  - country differs (when both known and cities don't match)
    const sameCity = geoCity && profileCity && geoCity === profileCity;
    const sameCountry = geoCountry && profileCountry && geoCountry === profileCountry;
    if (sameCity || (sameCountry && !geoCity && !profileCity)) return;
    if (!geoCity && !geoCountry) return;
    try {
      if (sessionStorage.getItem('geoToastShown') === '1') return;
    } catch {}
    setShowGeoToast(true);
    try { sessionStorage.setItem('geoToastShown', '1'); } catch {}
    const t = setTimeout(() => setShowGeoToast(false), 6000);
    return () => clearTimeout(t);
  }, [geoLocation, user]);
  
  const handleUseProfileCity = () => {
    if (!user?.residence_city && !user?.residence_country) return;
    const targetCity = user.residence_city || '';
    const targetCountry = user.residence_country || '';
    setOverrideCity(targetCity);
    setOverrideCountry(targetCountry);
    try {
      if (targetCity) localStorage.setItem('landingOverrideCity', targetCity);
      if (targetCountry) localStorage.setItem('landingOverrideCountry', targetCountry);
    } catch {}
    setShowGeoToast(false);
  };

  useEffect(() => {
    // Check if running as standalone app or already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    const alreadyInstalled = localStorage.getItem('pwa-installed');
    setIsStandalone(standalone || alreadyInstalled === 'true');

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event (Android/Desktop)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Listen for app installed event
    const handleAppInstalled = () => {
      // Check if we already showed the toast
      if (localStorage.getItem('install-toast-shown') === 'true') {
        return;
      }
      localStorage.setItem('pwa-installed', 'true');
      localStorage.setItem('install-toast-shown', 'true');
      setIsStandalone(true);
      setShowInstallPopup(false);
      // Show installation complete toast
      showLargeToast(
        'App Installed Successfully!',
        'ExpatsSphere has been added to your home screen. You can now access it anytime like a native app!',
        'success'
      );
    };
    
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Separate effect for showing the popup - only runs once
  useEffect(() => {
    if (popupInitialized) return;
    
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    const alreadyInstalled = localStorage.getItem('pwa-installed');
    const popupDismissedPermanent = localStorage.getItem('install-popup-dismissed');
    
    // Show popup for non-members who haven't dismissed it
    if (!standalone && alreadyInstalled !== 'true' && !popupDismissedPermanent && !user) {
      const timer = setTimeout(() => {
        setShowInstallPopup(true);
        setPopupInitialized(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
    
    setPopupInitialized(true);
  }, [user, popupInitialized]);

  // Hide popup when user logs in
  useEffect(() => {
    if (user) {
      setShowInstallPopup(false);
    }
  }, [user]);

  const dismissInstallPopup = () => {
    setShowInstallPopup(false);
    // Remember dismissal permanently using localStorage
    localStorage.setItem('install-popup-dismissed', 'true');
  };

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      // Mark as prompted for iOS
      localStorage.setItem('pwa-installed', 'true');
      setIsStandalone(true);
      return;
    }
    
    if (deferredPrompt) {
      // Just prompt - the success toast will show via 'appinstalled' event
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        localStorage.setItem('pwa-installed', 'true');
        setIsStandalone(true);
        // The 'appinstalled' event will show the completion toast
      }
      setDeferredPrompt(null);
    } else {
      // Android Chrome fallback - show instructions toast
      showLargeToast(
        'Install ExpatsSphere',
        'Tap the ⋮ menu (3 dots) in your browser, then tap "Add to Home Screen" or "Install App" to install.',
        'info'
      );
      localStorage.setItem('pwa-installed', 'true');
      setIsStandalone(true);
    }
  };

  const features = [
    {
      icon: Calendar,
      title: 'Explore Events',
      description: 'Find meetups, social gatherings, and networking events in your city',
      link: '/events',
      requiresAuth: false,
    },
    {
      icon: Users,
      title: 'Build Your Community',
      description: 'Connect with fellow expats who share your interests and experiences',
      link: isIOSApp ? '/find-expats' : '/membership',
      requiresAuth: false,
    },
    {
      icon: MapPin,
      title: 'Global Network',
      description: 'Access events across multiple countries and cities worldwide',
      link: '/find-expats',
      requiresAuth: false,
    },
    {
      icon: Sparkles,
      title: 'Host Your Own',
      description: 'Create and manage your own events, from casual meetups to formal gatherings',
      link: '/create-event',
      requiresAuth: false,
    },
  ];

  const [authMessage, setAuthMessage] = useState('');

  const handleFeatureClick = (feature, e) => {
    if (feature.requiresAuth && !user) {
      e.preventDefault();
      setAuthMessage(feature.authMessage);
      setShowAuth(true);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* One-time geolocation toast: lets users one-tap-switch to their profile city when traveling */}
      {showGeoToast && geoLocation && (user?.residence_city || user?.residence_country) && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-md w-[92vw] bg-white border-2 border-primary/20 shadow-xl rounded-2xl p-3 flex items-center gap-3 animate-in slide-in-from-top"
          data-testid="geo-toast"
        >
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <MapPin className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">
              Showing events in <strong>{geoLocation.city || geoLocation.country}</strong>
            </p>
            <button
              type="button"
              onClick={handleUseProfileCity}
              className="text-xs text-primary hover:underline mt-0.5"
              data-testid="geo-toast-use-profile"
            >
              Use {user.residence_city || user.residence_country} instead?
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowGeoToast(false)}
            className="text-muted-foreground hover:text-foreground shrink-0 p-1 rounded-full hover:bg-muted"
            data-testid="geo-toast-close"
            aria-label="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-24 pb-8 md:pt-32 md:pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto relative">
          {/* Pricing Badge */}
          {!user && !isIOSApp && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center mb-6 gap-2"
            >
              {/* Price Badge */}
              <Link to="/membership" className="max-w-full">
                <div className="pricing-badge-shimmer inline-flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-bold text-sm sm:text-lg shadow-lg border-2 border-white cursor-pointer hover:scale-105 transition-transform text-center whitespace-nowrap" data-testid="pricing-badge">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <span className="sm:hidden">Join FREE — 2 Years, No Card</span>
                  <span className="hidden sm:inline">Join FREE — 2 Full Years, No Card Needed</span>
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                </div>
              </Link>
              {/* Benefit Message */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">No credit card required • Full access • Cancel anytime</span>
              </div>
            </motion.div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold font-heading tracking-tighter leading-none text-foreground mb-6">
                Find Your People.
                <br />
                <span className="text-primary">Any</span><span className="text-accent italic">where</span>.
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-8 max-w-xl">
                <span className="text-accent font-bold" style={{ fontFamily: "'Satisfy', cursive" }}>Connecting Expats Globally</span> - discover events, make meaningful connections,
                and build lasting friendships in any city around the world.
              </p>

              {/* Live event counter — social proof under the hero */}
              {!loadingLocal && detectedCity && (() => {
                const allEvents = [...(localEvents || []), ...(countryEvents || [])];
                if (allEvents.length === 0) return null;
                const today = new Date();
                const todayStr = today.toISOString().split('T')[0];
                const weekEnd = new Date(today);
                weekEnd.setDate(weekEnd.getDate() + 7);
                const weekEndStr = weekEnd.toISOString().split('T')[0];
                
                let happeningToday = 0;
                let thisWeek = 0;
                for (const e of allEvents) {
                  const d = e.event_date;
                  if (!d) continue;
                  if (d === todayStr) happeningToday += 1;
                  if (d >= todayStr && d <= weekEndStr) thisWeek += 1;
                }
                
                if (thisWeek === 0 && happeningToday === 0) return null;
                
                const cityLabel = detectedCity;
                return (
                  <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium" data-testid="landing-live-counter">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span className="text-emerald-900">
                      <strong>{thisWeek}</strong> event{thisWeek !== 1 ? 's' : ''} in <strong>{cityLabel}</strong> this week
                      {happeningToday > 0 && (
                        <> · <strong>{happeningToday}</strong> happening today</>
                      )}
                    </span>
                  </div>
                );
              })()}
              
              {/* City override chip + picker — always visible when we know a city, regardless of events */}
              {detectedCity && (
                <div className="mb-3 flex flex-wrap items-center gap-2 max-w-xl" data-testid="landing-location-chip">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent/40 text-foreground rounded-full text-xs font-medium border border-border">
                    <MapPin className="w-3 h-3 text-primary" />
                    <span>
                      {locationSource === 'override' && `Browsing: ${detectedCity}`}
                      {locationSource === 'geo' && `📍 Current: ${detectedCity}`}
                      {locationSource === 'profile' && `Profile: ${detectedCity}`}
                      {!locationSource && `Detected: ${detectedCity}`}
                    </span>
                    {locationSource === 'override' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setOverrideCity('');
                          setOverrideCountry('');
                          try {
                            localStorage.removeItem('landingOverrideCity');
                            localStorage.removeItem('landingOverrideCountry');
                          } catch {}
                          setShowCityPicker(false);
                        }}
                        className="ml-1 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-primary rounded"
                        data-testid="landing-reset-city"
                      >
                        Reset
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setCityPickerInput('');
                          setCountryPickerInput('');
                          setShowCityPicker(true);
                        }}
                        className="ml-1 px-1.5 py-0.5 text-[10px] text-primary hover:underline rounded"
                        data-testid="landing-change-city"
                      >
                        Not yours? Change
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {showCityPicker && (
                <div className="mb-3 p-3 bg-white border border-border rounded-xl max-w-xl space-y-2" data-testid="landing-city-picker">
                  <p className="text-xs text-muted-foreground">Browse events in a different city:</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      placeholder="City (e.g., Lisbon)"
                      value={cityPickerInput}
                      onChange={(e) => setCityPickerInput(e.target.value)}
                      className="flex-1 min-w-[140px] px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:border-primary"
                      data-testid="landing-city-picker-city"
                      autoFocus
                    />
                    <input
                      type="text"
                      placeholder="Country (optional)"
                      value={countryPickerInput}
                      onChange={(e) => setCountryPickerInput(e.target.value)}
                      className="flex-1 min-w-[140px] px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:border-primary"
                      data-testid="landing-city-picker-country"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const c = cityPickerInput.trim();
                        const ct = countryPickerInput.trim();
                        if (!c && !ct) return;
                        setOverrideCity(c);
                        setOverrideCountry(ct);
                        try {
                          if (c) localStorage.setItem('landingOverrideCity', c); else localStorage.removeItem('landingOverrideCity');
                          if (ct) localStorage.setItem('landingOverrideCountry', ct); else localStorage.removeItem('landingOverrideCountry');
                        } catch {}
                        setShowCityPicker(false);
                        setDistanceKm('');
                      }}
                      className="px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                      data-testid="landing-city-picker-apply"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCityPicker(false)}
                      className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              {/* Be-the-first-host CTA — ONLY when we got a confirmed empty response for this city.
                  Previously this would flash during transient network errors because the catch
                  swallowed the failure silently. */}
              {!loadingLocal && eventsFetchOk && detectedCity && localEvents.length === 0 && countryEvents.length === 0 && (
                <div className="mb-6 max-w-xl rounded-2xl border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 via-accent/10 to-orange-50 p-5" data-testid="be-first-host-card">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-base mb-1" data-testid="be-first-host-title">
                        Be the first to host an event in {detectedCity}!
                      </h3>
                      <p className="text-xs text-muted-foreground mb-3">
                        No events here yet — start the local expat scene with a coffee meetup, language exchange, or hike. It only takes a minute.
                      </p>
                      <Link
                        to={user ? "/create-event" : "/login?redirect=/create-event"}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-sm font-semibold rounded-full hover:bg-primary/90 transition-colors"
                        data-testid="be-first-host-cta"
                      >
                        <Plus className="w-4 h-4" />
                        Create First Event
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Local Events Cards — right under tagline */}
              {!loadingLocal && (localEvents.length > 0 || countryEvents.length > 0) && (() => {
                const renderEventCard = (event) => (
                  <div key={event.event_id} data-testid={`landing-event-${event.event_id}`}>
                    <PrefetchLink to={`/events/${event.event_id}`} eventId={event.event_id}>
                      <div className="bg-white rounded-xl border border-border p-3 hover:shadow-md transition-shadow cursor-pointer group">
                        {event.image_url && (
                          <div className="relative">
                            <img src={event.image_url} alt={event.title} className="w-full h-24 object-contain bg-gray-50 rounded-lg mb-2" />
                            {typeof event.distance_km === 'number' && (
                              <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-white/95 text-gray-700 text-[10px] font-bold rounded-full shadow flex items-center gap-0.5" data-testid="landing-distance-badge">
                                <MapPin className="w-2.5 h-2.5" />
                                {event.distance_km} km
                              </span>
                            )}
                          </div>
                        )}
                        <h4 className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-1">{event.title}</h4>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Calendar className="w-3 h-3" />
                          <span>{event.event_date}{event.event_time ? ` at ${event.event_time}` : ''}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <MapPin className="w-3 h-3" />
                          <span className="line-clamp-1">{event.city}{event.country ? `, ${event.country}` : ''}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Users className="w-3 h-3" />
                          <span>
                            {calcHeadcount(event)} attending
                          </span>
                        </div>
                      </div>
                    </PrefetchLink>
                    {/* Recurring dates dropdown */}
                    {event._otherDates && event._otherDates.length > 0 && (
                      <div className="mt-1">
                        <button
                          onClick={(e) => { e.preventDefault(); setExpandedRecurring(prev => ({ ...prev, [event.event_id]: !prev[event.event_id] })); }}
                          className="flex items-center gap-1 text-xs text-primary hover:underline px-1"
                        >
                          <ChevronDown className={`w-3 h-3 transition-transform ${expandedRecurring[event.event_id] ? 'rotate-180' : ''}`} />
                          {event._otherDates.length} more date{event._otherDates.length > 1 ? 's' : ''}
                        </button>
                        {expandedRecurring[event.event_id] && (
                          <div className="mt-1 space-y-1 pl-2 border-l-2 border-primary/20">
                            {event._otherDates.map((d) => (
                              <Link to={`/events/${d.event_id}`} key={d.event_id} className="block text-xs text-muted-foreground hover:text-primary">
                                {d.event_date}{d.event_time ? ` at ${d.event_time}` : ''}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
                
                return (
                  <>
                    {/* Distance pills — visible only when we have a city to center on */}
                    {detectedCity && detectedCity === (user?.residence_city || detectedCity) && (
                      <div className="mb-3 flex flex-wrap items-center gap-1.5 max-w-xl" data-testid="landing-distance-pills">
                        <span className="text-xs text-muted-foreground mr-1">Within:</span>
                        {[
                          { v: '', label: 'City only' },
                          { v: '25', label: '25 km' },
                          { v: '50', label: '50 km' },
                          { v: '100', label: '100 km' },
                        ].map(opt => (
                          <button
                            key={opt.v || 'city'}
                            type="button"
                            onClick={() => setDistanceKm(opt.v)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              distanceKm === opt.v
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-white text-muted-foreground border-border hover:border-primary/40 hover:text-primary'
                            }`}
                            data-testid={`landing-distance-pill-${opt.v || 'city'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {localEvents.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-lg font-bold mb-3">
                          {distanceKm
                            ? `Events within ${distanceKm} km of ${detectedCity}`
                            : detectedCity ? `Events in ${detectedCity}` : 'Upcoming Events'}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl" data-testid="landing-city-events">
                          {localEvents.slice(0, 4).map(renderEventCard)}
                        </div>
                      </div>
                    )}
                    
                    {/* Other events in the same country (only when NOT in radius mode) */}
                    {!distanceKm && countryEvents.length > 0 && detectedCountry && (
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-3 max-w-xl">
                          <h3 className="text-lg font-bold">
                            {localEvents.length > 0
                              ? `Other events in ${detectedCountry}`
                              : `Events in ${detectedCountry}`}
                          </h3>
                          <Link to="/events" className="text-xs text-primary hover:underline">See all</Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl" data-testid="landing-country-events">
                          {countryEvents.slice(0, 4).map(renderEventCard)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Expats Near You — horizontal scroll, logged-in only */}
              {user && localExpats.length > 0 && (
                <div className="mb-6 max-w-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold">
                      {detectedCity ? `Expats in ${detectedCity}` : 'Expats Near You'}
                    </h3>
                    <Link to="/find-expats" className="text-xs text-primary hover:underline">See All</Link>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {localExpats.map((expat) => (
                      <Link to={`/profile/${expat.user_id}`} key={expat.user_id} className="flex-shrink-0">
                        <div className="w-24 text-center group">
                          <div className="w-14 h-14 mx-auto rounded-full overflow-hidden bg-primary/10 mb-1 border-2 border-white shadow-sm">
                            {expat.profile_picture ? (
                              <img src={expat.profile_picture} alt={expat.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-primary font-bold text-lg">
                                {(expat.name || '?')[0].toUpperCase()}
                              </div>
                            )}
                          </div>
                          <p className="font-medium text-xs line-clamp-1 group-hover:text-primary transition-colors">{expat.name}</p>
                          {expat.nationality && (
                            <p className="text-[10px] text-muted-foreground line-clamp-1">{expat.nationality}</p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                {user ? (
                  <>
                    <Link to="/events">
                      <Button
                        className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-10 rounded-full font-bold text-lg brutal-shadow"
                        data-testid="hero-browse-events"
                      >
                        Browse Events
                      </Button>
                    </Link>
                    <Link to="/create-event">
                      <Button
                        variant="outline"
                        className="h-14 px-10 rounded-full font-bold text-lg border-2"
                        data-testid="hero-create-event"
                      >
                        Create Event
                      </Button>
                    </Link>
                    {!window.matchMedia('(display-mode: standalone)').matches && !window.navigator.standalone && (
                      <Link to="/dashboard">
                        <Button
                          variant="outline"
                          className="h-14 px-10 rounded-full font-bold text-lg border-2"
                          data-testid="hero-my-events"
                        >
                          My Events
                        </Button>
                      </Link>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        setAuthMessage('Join ExpatsSphere FREE — 2 Full Years, No Card Needed!');
                        setShowAuth(true);
                      }}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 h-auto min-h-14 max-w-full w-full sm:w-auto px-4 sm:px-10 py-3 rounded-full font-bold text-sm sm:text-lg brutal-shadow whitespace-normal text-center leading-tight"
                      data-testid="hero-get-started"
                    >
                      <span className="sm:hidden">Join FREE — 2 Years, No Card</span>
                      <span className="hidden sm:inline">Join FREE — 2 Full Years, No Card Needed</span>
                    </Button>
                    <Link to="/events">
                      <Button
                        className="bg-accent hover:bg-accent/90 text-black h-14 px-10 rounded-full font-bold text-lg border-2 border-accent shadow-md"
                        data-testid="hero-explore"
                      >
                        Explore {eventCount > 0 ? `${eventCount} Events` : 'Events'}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </motion.div>

            <motion.div
              className="md:col-span-5"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/0e83dfda-7b0c-4dcd-9904-363461cc0167/images/9e1a268b00dd638b6bf4c4d51c037c66ae21b37e812be23f2f74ed3b3cab378a.png"
                  alt="Diverse group socializing at rooftop"
                  className="rounded-3xl w-full h-[400px] md:h-[500px] object-cover border-4 border-border brutal-shadow"
                />
              </div>
            </motion.div>
          </div>

          {/* Feature Bubbles - 2x2 Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-2xl mx-auto">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link to={feature.link} key={index} onClick={(e) => handleFeatureClick(feature, e)}>
                    <div className="flex flex-col items-center text-center cursor-pointer group">
                      <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-1 shadow-md border-2 border-white group-hover:scale-110 transition-transform">
                        <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                      </div>
                      <h3 className="text-xs md:text-sm font-semibold group-hover:text-primary transition-colors">{feature.title}</h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Everything You Need Section */}
      <section className="py-10 md:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-4xl font-bold font-heading tracking-tight mb-2">
              Everything You Need to Connect
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              Built for expats, by expats. All the tools you need to thrive in your new home.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {!user && (
        <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold font-heading tracking-tight mb-2">
                  Why Join ExpatsSphere?
                </h2>
                <p className="text-muted-foreground">Everything you need to thrive in your new home</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Benefit 1 */}
                <div className="bg-white rounded-2xl p-6 border-2 border-border shadow-md">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Connect Instantly</h3>
                  <p className="text-muted-foreground text-sm">
                    Find expats from your home country or with shared interests. Send connection requests and start chatting right away.
                  </p>
                </div>
                
                {/* Benefit 2 */}
                <div className="bg-white rounded-2xl p-6 border-2 border-border shadow-md">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4">
                    <Calendar className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Discover Local Events</h3>
                  <p className="text-muted-foreground text-sm">
                    Browse meetups, networking events, language exchanges, and social gatherings happening in your city every week.
                  </p>
                </div>
                
                {/* Benefit 3 */}
                <div className="bg-white rounded-2xl p-6 border-2 border-border shadow-md">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Global Community</h3>
                  <p className="text-muted-foreground text-sm">
                    Join groups based on your interests, nationality, or profession. Get notified when members host events near you.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section - For Logged In Users (Above Recommendations) */}
      {user && (
        <section className="pt-4 pb-2 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-secondary text-secondary-foreground rounded-3xl p-5 md:p-6 border-4 border-border brutal-shadow"
            >
              <h2 className="text-xl md:text-2xl font-bold font-heading mb-2">
                Ready to Start Your Journey?
              </h2>
              <p className="text-sm md:text-base mb-3 opacity-90">
                Join thousands of expats building meaningful connections worldwide.
              </p>
              <Link to="/create-event">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-white px-6 py-3 rounded-full font-bold text-base shadow-lg border-2 border-white cursor-pointer hover:scale-105 transition-transform">
                  <Calendar className="w-5 h-5" />
                  Create Event
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* AI Recommendations Section - For Logged In Users */}
      {user && (
        <section className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <AIRecommendations />
          </div>
        </section>
      )}

      {/* Install App Section */}
      {!isStandalone && (
        <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl p-6 md:p-8 border-2 border-border brutal-shadow"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center shadow-lg">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl md:text-2xl font-heading font-bold mb-2">
                    Install the App for FREE!
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Get the ExpatsSphere app for quick access and a native experience. Stay connected anytime, anywhere!
                  </p>
                  <Button
                    onClick={handleInstallClick}
                    className="bg-accent hover:bg-accent/90 text-black font-bold h-12 px-6 rounded-full text-base shadow-lg border-2 border-accent"
                    data-testid="install-app-button"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Install FREE App
                  </Button>
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2">Or download from:</p>
                    <AppStoreBadges size="sm" className="justify-center md:justify-start" />
                  </div>
                </div>
              </div>
              
              {/* iOS Instructions Modal */}
              {showIOSInstructions && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-6 p-6 bg-accent/10 rounded-2xl border border-accent/30"
                >
                  <h3 className="font-bold mb-3">How to install on iPhone/iPad:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Tap the <strong>Share</strong> button (square with arrow) at the bottom of Safari</li>
                    <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                    <li>Tap <strong>"Add"</strong> in the top right corner</li>
                    <li>The ExpatsSphere icon will appear on your home screen!</li>
                  </ol>
                  <Button
                    variant="ghost"
                    onClick={() => setShowIOSInstructions(false)}
                    className="mt-4 text-sm"
                  >
                    Got it!
                  </Button>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section - For Non-Logged In Users Only */}
      {!user && (
        <section className="py-10 md:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-secondary text-secondary-foreground rounded-3xl p-8 md:p-10 border-4 border-border brutal-shadow"
            >
              <h2 className="text-2xl md:text-4xl font-bold font-heading mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-base md:text-lg mb-6 opacity-90">
                Join thousands of expats building meaningful connections worldwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  onClick={() => {
                    setAuthMessage(isIOSApp ? 'Join ExpatsSphere today!' : 'Join FREE — 2 Full Years, No Card Needed!');
                    setShowAuth(true);
                  }}
                  className="bg-accent text-accent-foreground hover:bg-accent/90 h-auto min-h-12 max-w-full w-full sm:w-auto px-4 sm:px-8 py-3 rounded-full font-bold text-sm sm:text-base brutal-shadow whitespace-normal text-center leading-tight"
                  data-testid="cta-get-started"
                >
                  {isIOSApp ? (
                    'Get Started Free'
                  ) : (
                    <>
                      <span className="sm:hidden">Join FREE — 2 Years, No Card</span>
                      <span className="hidden sm:inline">Join FREE — 2 Full Years, No Card Needed</span>
                    </>
                  )}
                </Button>
                {!isIOSApp && <span className="text-sm opacity-70">No credit card required</span>}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      <Footer />

      {/* Install App Popup */}
      {showInstallPopup && !isStandalone && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={dismissInstallPopup}
        >
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 25 }}
            className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl border-2 border-accent"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Smartphone className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold mb-2">
                Install ExpatsSphere FREE!
              </h3>
              <p className="text-muted-foreground mb-6">
                Get quick access to events, connect with expats, and stay updated - all from your home screen!
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    handleInstallClick();
                    dismissInstallPopup();
                  }}
                  className="w-full bg-accent hover:bg-accent/90 text-black font-bold h-14 rounded-full text-lg shadow-lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Install FREE App
                </Button>
                <Button
                  onClick={dismissInstallPopup}
                  variant="ghost"
                  className="w-full text-muted-foreground"
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {showAuth && <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} customMessage={authMessage} defaultToSignup={true} />}
      
      <TutorialTour 
        open={showTutorial} 
        onClose={() => setShowTutorial(false)}
        onComplete={() => setShowTutorial(false)}
      />
    </div>
  );
};

export default Landing;